import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';

import { environment } from '@environments/environment';
import { ApiResponse } from '@core/types';
import { PaginationConfig, PaginationResult } from './unit.service';
import { Resident } from '@core/types/unit.type';
import {
  CouncilMember,
  CouncilMemberCreate,
  CouncilMemberFromResidentCreate,
  CouncilMemberUpdate,
} from '@core/types/council.type';

@Injectable({ providedIn: 'root' })
export class CouncilService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.api}/council`;

  grid(config: PaginationConfig): Observable<ApiResponse<PaginationResult<CouncilMember>>> {
    let params = new HttpParams()
      .set('pageNumber', config.pageNumber.toString())
      .set('pageSize', config.pageSize.toString());

    if (config.searchTerm) params = params.set('searchTerm', config.searchTerm);

    // Filter is passed as filter.xxx
    if (config.filter) {
      Object.entries(config.filter as Record<string, any>).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') return;
        params = params.set(`filter.${k}`, String(v));
      });
    }

    return this.http.get<ApiResponse<PaginationResult<CouncilMember>>>(`${this.baseUrl}/grid`, { params }).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading council members')
      )
    );
  }

  get(id: string): Observable<ApiResponse<CouncilMember>> {
    return this.http.get<ApiResponse<CouncilMember>>(`${this.baseUrl}/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading council member')
      )
    );
  }

  create(data: CouncilMemberCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(this.baseUrl, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error creating council member')
      )
    );
  }

  update(data: CouncilMemberUpdate): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(this.baseUrl, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error updating council member')
      )
    );
  }

  delete(id: string): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${id}`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error deleting council member')
      )
    );
  }

  getActiveResidents(): Observable<ApiResponse<Resident[]>> {
    return this.http.get<ApiResponse<Resident[]>>(`${this.baseUrl}/active-residents`).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error loading active residents')
      )
    );
  }

  createFromResident(data: CouncilMemberFromResidentCreate): Observable<ApiResponse<string>> {
    return this.http.post<ApiResponse<string>>(`${this.baseUrl}/from-resident`, data).pipe(
      catchError((errorResponse: HttpErrorResponse) =>
        throwError(() => errorResponse.error?.message || 'Error creating council member from resident')
      )
    );
  }
}

